import { QueryClient, useMutation, useQuery, useQueryClient, keepPreviousData, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      retry: (count, err) => {
        const status = (err as ApiError).status;
        return count < 2 && (status === 0 || status >= 500);
      },
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
});

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

/** GET with query params; key = [url, params]. */
export function useApi<T>(url: string | null, params?: Record<string, unknown>, opts: { enabled?: boolean; refetchInterval?: number; keepPrevious?: boolean } = {}) {
  return useQuery<T, ApiError>({
    queryKey: [url, params ?? {}],
    queryFn: () => api.get<T>(url!, params),
    enabled: !!url && (opts.enabled ?? true),
    refetchInterval: opts.refetchInterval,
    placeholderData: opts.keepPrevious === false ? undefined : keepPreviousData,
  });
}

export function useList<T>(url: string | null, params?: Record<string, unknown>, opts?: { enabled?: boolean }) {
  return useApi<Paginated<T>>(url, params, opts);
}

type Method = 'post' | 'patch' | 'put' | 'delete' | 'upload';

/**
 * Mutation helper: shows a success toast, surfaces API errors, and invalidates queries whose URL key starts with
 * any of `invalidate` prefixes (e.g. ['/employees']).
 */
export function useApiMutation<TBody = unknown, TResult = unknown>(
  method: Method,
  url: string | ((body: TBody) => string),
  opts: { success?: string | ((r: TResult) => string); invalidate?: string[]; onSuccess?: (r: TResult, body: TBody) => void; silentError?: boolean } = {},
) {
  const qc = useQueryClient();
  return useMutation<TResult, ApiError, TBody>({
    mutationFn: (body) => {
      const u = typeof url === 'function' ? url(body) : url;
      if (method === 'delete') return api.delete<TResult>(u);
      if (method === 'upload') return api.upload<TResult>(u, body as Record<string, unknown>);
      return api[method]<TResult>(u, body);
    },
    onSuccess: (r, body) => {
      if (opts.success) toast.success(typeof opts.success === 'function' ? opts.success(r) : opts.success);
      if (opts.invalidate?.length) invalidatePrefixes(qc, opts.invalidate);
      opts.onSuccess?.(r, body);
    },
    onError: (e) => {
      if (!opts.silentError) toast.error(e.message, { description: e.requestId ? `Ref: ${e.requestId.slice(0, 8)}` : undefined });
    },
  });
}

export function invalidatePrefixes(qc: QueryClient, prefixes: string[]) {
  return qc.invalidateQueries({
    predicate: (q) => {
      const key = (q.queryKey as QueryKey)[0];
      return typeof key === 'string' && prefixes.some((p) => key.startsWith(p));
    },
  });
}
